import {
  FEATURE_FLAG_STATUS,
  FeatureFlagEnvironment,
  FeatureFlagItem,
  FeatureFlagStatus,
} from '../../../lib/api/featureFlag/types';

export const STATUS_LABEL: Record<FeatureFlagStatus, string> = {
  [FEATURE_FLAG_STATUS.IN_DEVELOPMENT]: '개발 중',
  [FEATURE_FLAG_STATUS.READY_FOR_QA]: 'QA',
  [FEATURE_FLAG_STATUS.READY_FOR_RELEASE]: '출시',
};

/** 설명 앞 [SCH][UW] 꼬리표 → 그 플래그를 쓰는 앱. 레포 관례(JIRA_TICKETS description) */
const APP_TAG_LABEL: Record<string, string> = {
  SCH: 'scheduler',
  UW: 'universal-worker',
  USER: 'user',
  ADMIN: 'admin',
  ONE: 'one',
  CIF: 'cif',
  FIF: 'fif',
  EW: 'excel-worker',
};

/** 설명 맨 앞의 [XX][YY] 를 앱 이름으로 떼어 낸다 — 모르는 꼬리표는 그대로 보여 준다 */
export const splitDescription = (description: string | null): { apps: string[]; text: string } => {
  if (!description) return { apps: [], text: '' };
  const match = description.match(/^((?:\[[A-Za-z]+\])+)\s*/);
  if (!match) return { apps: [], text: description };
  const apps = [...match[1].matchAll(/\[([A-Za-z]+)\]/g)].map(([, tag]) => APP_TAG_LABEL[tag.toUpperCase()] ?? tag);
  return { apps, text: description.slice(match[0].length) };
};

const ENVIRONMENT_LABEL: Record<FeatureFlagEnvironment, string> = {
  local: 'LOCAL',
  development: 'DEV',
  production: 'PROD',
  test: 'TEST',
};

const isKnownEnvironment = (environment: string): environment is FeatureFlagEnvironment =>
  Object.prototype.hasOwnProperty.call(ENVIRONMENT_LABEL, environment);

/** 모르는 값은 원문 그대로 — 빈 배지로 dev 처럼 보이지 않게 */
export const environmentLabel = (environment: string): string =>
  isKnownEnvironment(environment) ? ENVIRONMENT_LABEL[environment] : environment.toUpperCase() || '알 수 없음';

/** 바꾸기 전에 조심시킬 환경 — prod 와, 운영일 수도 있는 모르는 값 */
export const isCautionEnvironment = (environment: string): boolean =>
  environment === 'production' || !isKnownEnvironment(environment);

export const onOff = (enabled: boolean): string => (enabled ? 'ON' : 'OFF');

/** 덮어쓴 값 표시 — null 이면 기본값 따름 */
export const overrideLabel = (dbEnabled: boolean | null): string =>
  dbEnabled === null ? '기본값 따름' : `덮어씀 · ${onOff(dbEnabled)}`;

/** 스위치를 target 으로 두면 서버가 남길 덮어쓴 값 — 기본값과 같으면 지운다(null) */
export const nextOverride = (flag: FeatureFlagItem, target: boolean): boolean | null =>
  target === flag.defaultEnabled ? null : target;

/** 이력 한 줄의 「덮어쓴 값」 → 사람이 읽는 말 */
export const historyValueLabel = (value: boolean | null): string =>
  value === null ? '기본값' : `덮어씀 · ${onOff(value)}`;

/** 상태값 줄의 짧은 설명 — 기본값이 어떻게 정해지는지 */
export const defaultHint = (flag: FeatureFlagItem): string =>
  flag.status === FEATURE_FLAG_STATUS.IN_DEVELOPMENT ? '로컬에서만' : onOff(flag.defaultEnabled);
